import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { trackEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";

const leadTypes = ["family_care", "senior_self_referral", "caregiver_application", "contact"] as const;

/**
 * Stored on phone-only leads because leads.email is NOT NULL. This mailbox
 * does not exist — never email it. Confirmation emails are skipped for leads
 * carrying this placeholder.
 */
export const PHONE_ONLY_EMAIL_PLACEHOLDER = "no-email-provided@kinshipcarecollective.com";

const leadSchema = z.object({
  full_name: z.string().trim().min(2, "Name must be at least 2 characters").max(200),
  email: z.string().trim().email("Please enter a valid email address").max(255),
  phone: z.string().trim().min(7).max(20).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional(),
  lead_type: z.enum(leadTypes),
  care_type_needed: z.string().trim().max(200).optional(),
  hours_needed: z.string().trim().max(200).optional(),
  experience: z.string().trim().max(2000).optional(),
  availability: z.string().trim().max(500).optional(),
  desired_hourly_rate: z.string().trim().max(50).optional(),
  certifications: z.string().trim().max(1000).optional(),
  resume_path: z.string().trim().max(500).optional(),
  lead_source: z.string().trim().max(120).optional(),
  lead_source_details: z.string().trim().max(300).optional(),
});

type LeadData = z.infer<typeof leadSchema>;

export const submitLead = async (data: LeadData) => {
  const parsed = leadSchema.safeParse(data);
  if (!parsed.success) {
    const firstError = parsed.error.issues[0]?.message || "Invalid input";
    throw new Error(firstError);
  }

  const clean = parsed.data;
  const attribution = getAttribution();

  const id = crypto.randomUUID();
  const { error } = await supabase.from("leads").insert([{
    id,
    full_name: clean.full_name,
    email: clean.email,
    lead_type: clean.lead_type,
    phone: clean.phone || null,
    message: clean.message || null,
    care_type_needed: clean.care_type_needed || null,
    hours_needed: clean.hours_needed || null,
    experience: clean.experience || null,
    availability: clean.availability || null,
    desired_hourly_rate: clean.desired_hourly_rate || null,
    certifications: clean.certifications || null,
    resume_path: clean.resume_path || null,
    lead_source: clean.lead_source || null,
    lead_source_details: clean.lead_source_details || null,
    utm_source: attribution.utm_source || null,
    utm_medium: attribution.utm_medium || null,
    utm_campaign: attribution.utm_campaign || null,
    landing_page: attribution.landing_page || null,
    referrer: attribution.referrer || null,
  }]);
  if (error) throw error;

  // GA4 conversion event — fires once per successful lead submission.
  // Use `generate_lead` (a GA4 recommended event) so it appears in the
  // standard conversions/reporting surfaces, plus a granular per-form event.
  const formMap: Record<typeof clean.lead_type, string> = {
    family_care: "find_care_form",
    senior_self_referral: "find_care_form",
    caregiver_application: "apply_now_form",
    contact: "contact_form",
  };
  const formName = formMap[clean.lead_type];
  trackEvent("generate_lead", {
    lead_type: clean.lead_type,
    form_name: formName,
    lead_id: id,
    lead_source: clean.lead_source || "unknown",
  });
  trackEvent(`${formName}_submit`, { lead_type: clean.lead_type, lead_id: id });

  // Send confirmation email via a dedicated public endpoint that re-reads
  // the lead from the database server-side. The underlying send function
  // is locked to service_role and cannot be called directly from the client.
  // Fire-and-forget — don't block form submission. Skipped for phone-only
  // leads, whose stored email is a non-existent placeholder mailbox.
  if (clean.email !== PHONE_ONLY_EMAIL_PLACEHOLDER) {
    supabase.functions.invoke("send-lead-confirmation", {
      body: { leadId: id },
    }).catch((err) => console.warn("Email send failed:", err));
  }

  // Internal inbox notification so every inquiry lands in the team inbox,
  // not just the leads table. Fire-and-forget.
  supabase.functions.invoke("notify-lead", {
    body: { leadId: id },
  }).catch((err) => console.warn("Lead notification failed:", err));

  return id;
};
